/**
 * DimensionLineHandle - Handle for placing the dimension line of a linear
 * dimension. It sits at the middle of that line, just on the other side from
 * the text so it never hides the value.
 *
 * Only the dimension line moves: the measured points stay fixed, the extension
 * lines follow. No ghost here (unlike MLeaderTextHandle): a dimension only
 * draws once its block is recomputed, which needs the entity in the database,
 * so the original itself is updated during the drag — a single dimension
 * block is cheap to rebuild.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D } from "../types";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
} from "../types";
import { AbstractHandle } from "./AbstractHandle";
import { MoveDimensionLineCommand } from "../../commands/MoveDimensionLineCommand";
import {
  dimensionLineMidpoint,
  dimensionTextSide,
  getDimensionPoints,
  setDimensionLinePoint,
  type DimensionKind,
} from "../../dimensions";

/** Screen gap between the dimension line and the edge of the handle. */
const LINE_GAP_PX = 4;

export class DimensionLineHandle extends AbstractHandle {
  readonly type = HandleType.Translation;
  readonly position = HandlePosition.Center;

  private entityId: DrawingModule.OdDbObjectId;

  /** Entity handle string for undo/redo */
  private entityHandle: string;

  private kind: DimensionKind;
  private a: Point3D;
  private b: Point3D;

  /** Middle of the dimension line; the handle is drawn beside it. */
  private lineMidpoint: Point3D;

  /** Dimension line point before the drag started */
  private originalPoint: Point3D | null = null;

  /** Dimension line point during the drag */
  private currentPoint: Point3D | null = null;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityId: DrawingModule.OdDbObjectId,
    dimension: {
      kind: DimensionKind;
      a: Point3D;
      b: Point3D;
      dimLinePoint: Point3D;
    },
  ) {
    super(viewer, history);
    this.entityId = entityId;
    this.entityHandle = this.getEntityHandle(entityId);
    this.kind = dimension.kind;
    this.a = dimension.a;
    this.b = dimension.b;

    // Same styling as TranslationHandle
    this.drawOptions = {
      fillColor: TRANSFORM_THEME.handleFill,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius: HANDLE_SIZES.translation * HANDLE_SIZES.handleScale,
      icon: "move",
    };

    this.lineMidpoint = dimensionLineMidpoint(
      this.kind,
      this.a,
      this.b,
      dimension.dimLinePoint,
    );
    this.placeBesideLine();
  }

  /** The offset is in pixels: recompute it for the current zoom. */
  beforeRedraw(): void {
    this.placeBesideLine();
  }

  private placeBesideLine(): void {
    const side = dimensionTextSide(this.kind, this.a, this.b);
    const offset = this.viewer.screenDistanceToWorld(
      this.drawOptions.radius + LINE_GAP_PX,
    );
    this.worldPosition = {
      x: this.lineMidpoint.x - side.x * offset,
      y: this.lineMidpoint.y - side.y * offset,
      z: 0,
    };
  }

  start(): void {
    if (this.isTransforming) return;

    const points = getDimensionPoints(this.module, this.entityId);
    if (!points) return;

    this.isTransforming = true;
    this.originalPoint = points.dimLinePoint;
    this.currentPoint = { ...points.dimLinePoint };
  }

  ongoing(context: TransformContext): void {
    if (!this.isTransforming || !this.originalPoint) return;

    const dx = context.worldPoint.x - context.startWorldPoint.x;
    const dy = context.worldPoint.y - context.startWorldPoint.y;
    const point: Point3D = {
      x: this.originalPoint.x + dx,
      y: this.originalPoint.y + dy,
      z: this.originalPoint.z,
    };

    setDimensionLinePoint(this.module, this.entityId, point);
    this.currentPoint = point;
    this.lineMidpoint = dimensionLineMidpoint(this.kind, this.a, this.b, point);
    this.placeBesideLine();

    this.viewer.throttledUpdate();
  }

  end(): void {
    if (!this.isTransforming || !this.originalPoint || !this.currentPoint) {
      this.cancel();
      return;
    }
    this.isTransforming = false;

    const from = this.originalPoint;
    const to = this.currentPoint;
    this.originalPoint = null;
    this.currentPoint = null;

    if (Math.abs(to.x - from.x) < 0.001 && Math.abs(to.y - from.y) < 0.001) {
      // No significant movement - no command needed
      this.safeViewerUpdate("DimensionLineHandle.end/no-move", {
        entityHandle: this.entityHandle,
      });
      return;
    }

    const command = new MoveDimensionLineCommand(this.viewer, {
      entityHandle: this.entityHandle,
      fromPoint: from,
      toPoint: to,
    });
    command.markAsExecuted();
    this.history.execute(command);

    this.safeViewerUpdate("DimensionLineHandle.end", {
      entityHandle: this.entityHandle,
    });
  }

  cancel(): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;

    if (this.originalPoint) {
      setDimensionLinePoint(this.module, this.entityId, this.originalPoint);
      this.lineMidpoint = dimensionLineMidpoint(
        this.kind,
        this.a,
        this.b,
        this.originalPoint,
      );
      this.placeBesideLine();
    }
    this.originalPoint = null;
    this.currentPoint = null;

    this.safeViewerUpdate("DimensionLineHandle.cancel", {
      entityHandle: this.entityHandle,
    });
  }
}
